/**
 * 비로그인 목록을 계정으로 옮긴다.
 *
 * 로컬 Pick은 사진을 **IndexedDB에 Blob으로** 들고 있다. 계정에 저장하려면
 * 먼저 올려서 주소를 받아야 하므로, 이관은 "업로드 → 저장" 두 단계다.
 *
 * 순서가 중요하다: 업로드가 다 끝난 뒤에 한 번에 저장하고, 저장까지 성공한
 * 뒤에야 로컬을 비운다. 중간에 실패하면 **아무것도 지우지 않는다** — 사용자
 * 입장에서 다시 시도할 수 있어야 한다.
 */

import type { PickInput } from '@lifetimeline/shared';

import { getLocalImage } from '../../lib/imageStore.js';
import { outputType } from '../../lib/imageFile.js';
import { uploadImage } from '../../lib/imageUpload.js';
import type { LocalPick } from '../../lib/localPicks.js';

/**
 * 사진이 있는 것만 올리고, 없는 것은 그대로 옮긴다.
 *
 * `hasImage`가 true인데 Blob이 없는 경우도 있다(저장소가 비워졌거나 시크릿
 * 창이었거나). 그때도 **버리지 않고 사진 없이 옮긴다** — 사용자가 고른 것은
 * 사진이 아니라 그 항목이다.
 */
export async function uploadLocalPicks(picks: readonly LocalPick[]): Promise<PickInput[]> {
  const ready: PickInput[] = [];

  for (const pick of picks) {
    const blob = pick.hasImage ? await getLocalImage(pick.categoryId) : null;
    const itemImageUrl = blob === null ? undefined : await uploadImage(blob, outputType());

    ready.push({
      categoryId: pick.categoryId,
      itemName: pick.itemName,
      ...(pick.itemDescription === undefined ? {} : { itemDescription: pick.itemDescription }),
      ...(itemImageUrl === undefined ? {} : { itemImageUrl }),
    });
  }

  return ready;
}
